/**
 * CodeBlock — the project-standard code/sample renderer.
 * =====================================================
 *
 * One consistent, syntax-highlighted, copyable code block used everywhere we
 * show source/snippets (curl examples, JSON payloads, SQL, config, etc.).
 * Prefer this over ad-hoc <pre> blocks so every snippet looks identical.
 *
 * Features:
 *   - Prism syntax highlighting, theme-aware (oneDark in dark mode, oneLight
 *     in light mode) so colors always read well against the app background.
 *   - Optional header strip with a title (e.g. "POST /endpoint") + language
 *     badge; a one-click Copy button with a "Copied" confirmation.
 *   - Horizontal scroll for long lines (default) or soft-wrap via ``wrap``.
 *
 * Usage:
 *   <CodeBlock code={curl} language="bash" title="Events · POST /events" />
 *   <CodeBlock code={json} language="json" />
 *
 * Only a focused set of languages is registered (keeps the bundle small).
 * Add more with SyntaxHighlighter.registerLanguage below as needed.
 */

import React, { useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';

SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('ts', typescript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('py', python);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('yaml', yaml);

const copyToClipboard = (text) => {
  try { navigator.clipboard.writeText(text); } catch (_e) { /* ignore */ }
};

export default function CodeBlock({
  code,
  language = 'bash',
  title,
  showCopy = true,
  wrap = false,
  sx,
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [copied, setCopied] = useState(false);

  const text = String(code ?? '');
  const onCopy = () => {
    copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // GitHub-ish panel: deep slate in dark mode, soft gray in light mode. Prism
  // token colors (oneDark/oneLight) read cleanly on each.
  const panelBg = dark ? '#0d1117' : '#f6f8fa';
  const headerBg = dark ? alpha('#ffffff', 0.04) : alpha('#000000', 0.03);

  const CopyButton = (
    <Tooltip title={copied ? 'Copied' : 'Copy'}>
      <IconButton
        size="small"
        onClick={onCopy}
        sx={{
          color: copied ? theme.palette.success.main : 'text.secondary',
          '&:hover': { color: copied ? theme.palette.success.main : 'text.primary' },
        }}
      >
        {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: panelBg,
        ...sx,
      }}
    >
      {title && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            bgcolor: headerBg,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'monospace',
              fontSize: 11.5,
              fontWeight: 600,
              color: dark ? alpha('#fff', 0.75) : alpha('#000', 0.7),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
          {language && (
            <Chip
              label={language}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
          {showCopy && CopyButton}
        </Box>
      )}

      <Box sx={{ position: 'relative' }}>
        {!title && showCopy && (
          <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}>{CopyButton}</Box>
        )}
        <SyntaxHighlighter
          language={language}
          style={dark ? oneDark : oneLight}
          wrapLongLines={wrap}
          customStyle={{
            margin: 0,
            padding: title ? '12px 14px' : '12px 40px 12px 14px',
            background: 'transparent',
            fontSize: 12.5,
            lineHeight: 1.6,
          }}
          codeTagProps={{
            style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
          }}
        >
          {text}
        </SyntaxHighlighter>
      </Box>
    </Box>
  );
}
